import { BASE_URL } from "../data/constants";
import type { MapFeedItemDTO } from "../data/types";
import { fetchMapFeed } from "./FeedService";

export type SongHit = {
  id: string;
  title: string;
  artist: string;
  albumCover?: string;
  previewUrl?: string;
  year?: string;
  genre?: string;
};

type SearchResponse = {
  data: SongHit[];
  total: number;
};

/**
 * Söker i Beatspots egen katalog, som bara innehåller det AudD har svarat med
 * vid igenkänning. Vi frågar alltså aldrig någon extern musiktjänst — låtar
 * som ingen sensor har hört går inte att hitta här.
 */
export const searchSong = async (q: string): Promise<SongHit[]> => {
  try {
    const res = await fetch(
      `${BASE_URL}/api/search?q=${encodeURIComponent(q)}`,
      { headers: { Accept: "application/json" } }
    );

    if (!res.ok) throw new Error(`Search failed: ${res.status}`);

    const json: SearchResponse = await res.json();

    return (json.data ?? []).map((item) => ({
      id: String(item.id),
      title: item.title,
      artist: item.artist ?? "Unknown artist",
      albumCover: item.albumCover ?? undefined,
      previewUrl: item.previewUrl ?? undefined,
      year: item.year ?? undefined,
      genre: undefined,
    }));
  } catch (err) {
    console.error("Search failed", err);
    return [];
  }
};

export type VenueHit = {
  id: string;
  name: string;
  address: string | null;
  imageUrl: string | null;
  item: MapFeedItemDTO;
};

function norm(s: string) {
  return s.trim().toLocaleLowerCase("sv-SE");
}

function matches(haystack: string | null | undefined, needle: string) {
  return Boolean(haystack) && norm(haystack as string).includes(needle);
}

/**
 * Ställen på namn eller adress. Samma princip som i native-appen: det finns
 * ingen sök-endpoint, men /mapfeed bär alla aktiva ställen med koordinater,
 * så filtreringen sker här.
 */
export function filterVenues(items: MapFeedItemDTO[], query: string): VenueHit[] {
  const q = norm(query);
  if (!q) return [];

  return items
    .filter((i) => matches(i.venue.name, q) || matches(i.venue.address, q))
    .map((i) => ({
      id: i.venue.id,
      name: i.venue.name,
      address: i.venue.address,
      imageUrl: i.venue.imageUrl,
      item: i,
    }));
}

const DIRECTORY_TTL_MS = 30_000;
let directory: { at: number; items: Promise<MapFeedItemDTO[]> } | null = null;

/**
 * Söket körs vid varje (debouncad) tangenttryckning. Flödet med alla ställen
 * är för tungt för det, så det hämtas högst var 30:e sekund och delas.
 * Ett misslyckat svar sparas inte.
 */
function loadVenueDirectory(): Promise<MapFeedItemDTO[]> {
  if (directory && Date.now() - directory.at < DIRECTORY_TTL_MS) {
    return directory.items;
  }

  const items = fetchMapFeed({ limit: 500 }).then((feed) =>
    Array.isArray(feed?.items) ? feed.items : []
  );

  const entry = { at: Date.now(), items };
  directory = entry;
  items.catch(() => {
    if (directory === entry) directory = null;
  });

  return items;
}

export async function searchVenues(query: string): Promise<VenueHit[]> {
  return filterVenues(await loadVenueDirectory(), query);
}

/** Bara för tester. */
export function resetVenueDirectory() {
  directory = null;
}
